import type {Content, ImageField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import type {SliceComponentProps} from '@prismicio/react'
import type {CustomerStoryDocument} from 'prismicio-types'
import {getCustomerStoryData} from 'src/common/helpers'
import type {Context} from 'src/common/types'
import {DefaultSocialProofBar} from './DefaultSocialProofBar'

export type SocialProofBarProps = SliceComponentProps<Content.SocialProofBarSlice>

const resolveCustomer = (
  relationship: Content.SocialProofBarSliceDefaultItem['customer'],
  customerStoriesData?: CustomerStoryDocument[],
): {companyName: string; companyLogo: ImageField; heroImage: ImageField; url: string | null} | null => {
  if (!isFilled.contentRelationship(relationship)) {
    return null
  }

  const data = getCustomerStoryData({customerStoriesData, customer: relationship})

  if (!data) {
    return null
  }

  return {
    companyName: isFilled.keyText(data.company_name) ? data.company_name : '',
    companyLogo: data.company_logo,
    heroImage: data.image,
    url: relationship.url ?? null,
  }
}

const resolveFeaturedCustomer = (
  relationship: Content.SocialProofBarSliceDefaultPrimary['featured_customer'],
  customerStoriesData?: CustomerStoryDocument[],
): {companyLogo: ImageField; image: ImageField; url: string | null} | null => {
  if (!isFilled.contentRelationship(relationship)) {
    return null
  }

  const data = getCustomerStoryData({customerStoriesData, customer: relationship})

  if (!data) {
    return null
  }

  return {
    companyLogo: data.company_logo,
    image: data.image,
    url: relationship.url ?? null,
  }
}

const SocialProofBar = ({
  slice,
  context: {customerStoriesData},
}: SocialProofBarProps & {context: Context}): JSX.Element | null => {
  switch (slice.variation) {
    case 'default':
      return (
        <DefaultSocialProofBar
          heading={isFilled.keyText(slice.primary.heading) ? slice.primary.heading : ''}
          featuredCustomer={resolveFeaturedCustomer(slice.primary.featured_customer, customerStoriesData)}
          featuredStatNumber={
            isFilled.keyText(slice.primary.featured_stat_number) ? slice.primary.featured_stat_number : ''
          }
          featuredStatDescription={
            isFilled.keyText(slice.primary.featured_stat_description) ? slice.primary.featured_stat_description : ''
          }
          bottomLabel={isFilled.keyText(slice.primary.bottom_label) ? slice.primary.bottom_label : ''}
          paddingTop={slice.primary.padding_top}
          paddingBottom={slice.primary.padding_bottom}
          reviews={slice.primary.reviews.map((review) => ({
            badgeIcon: review.badge_icon,
            source: isFilled.keyText(review.source) ? review.source : '',
            rating: isFilled.keyText(review.rating) ? review.rating : '',
            description: isFilled.keyText(review.description) ? review.description : '',
          }))}
          customers={slice.items
            .map((item) => resolveCustomer(item.customer, customerStoriesData))
            .filter((customer): customer is NonNullable<typeof customer> => customer !== null)}
        />
      )
    default:
      return null
  }
}

export default SocialProofBar
